import { BBox } from "@turf/turf";
import { type LngLatBoundsLike } from "maplibre-gl";
import { type MapRef } from "react-map-gl/maplibre";

/**
 * Narrows a GeoJSON position to the [lng, lat] pair MapLibre centres on, or null if it
 * isn't a usable position. Positions from imported GPS tracks carry a third Z/elevation
 * element; the strict `length === 2` tests this replaces rejected them, so a 3D geometry
 * produced no centre and the map never moved to the selected feature.
 */
const toLngLat = (position: any): [number, number] | null => {
  if (!Array.isArray(position) || position.length < 2) return null;
  const [lng, lat] = position;
  if (typeof lng !== "number" || typeof lat !== "number" || !isFinite(lng) || !isFinite(lat)) return null;
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
  return [lng, lat];
};

/** A position that carries real location data — [0, 0] is the "unset geometry" sentinel. */
const isNonNullIsland = (position: [number, number]) => position[0] !== 0 || position[1] !== 0;

/** The first position in a ring/line that is a real, in-range location. */
const findFirstUsablePosition = (positions: any): [number, number] | null => {
  if (!Array.isArray(positions)) return null;
  for (const position of positions) {
    const lngLat = toLngLat(position);
    if (lngLat && isNonNullIsland(lngLat)) return lngLat;
  }
  return null;
};

/**
 * Validate and process geometry, extracting center coordinates
 */
export const validateAndProcessGeometry = (
  geoJSONGeometry: any
): {
  hasValidCoordinates: boolean;
  center: [number, number];
  zoom: number;
} => {
  let center: [number, number] = [0, 0];
  let zoom = 7;
  let hasValidCoordinates = false;

  if (!geoJSONGeometry) {
    return { hasValidCoordinates, center, zoom };
  }

  try {
    if (geoJSONGeometry.type === "Point" && Array.isArray(geoJSONGeometry.coordinates)) {
      const point = toLngLat(geoJSONGeometry.coordinates);
      if (point && isNonNullIsland(point)) {
        center = point;
        zoom = 14;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid Point coordinates:", { center, zoom });
      } else {
        console.warn("[MAP-ZOOM] Invalid coordinate ranges:", geoJSONGeometry.coordinates);
      }
    } else if (
      geoJSONGeometry.type === "LineString" &&
      Array.isArray(geoJSONGeometry.coordinates) &&
      geoJSONGeometry.coordinates.length > 0
    ) {
      // Find first non-zero coordinate
      const validPoint = findFirstUsablePosition(geoJSONGeometry.coordinates);
      if (validPoint) {
        center = validPoint;
        zoom = 12;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid LineString coordinates:", { center, zoom });
      }
    } else if (
      geoJSONGeometry.type === "MultiLineString" &&
      Array.isArray(geoJSONGeometry.coordinates) &&
      geoJSONGeometry.coordinates.length > 0
    ) {
      // Extract all points and find first valid one
      for (const lineString of geoJSONGeometry.coordinates) {
        const validPoint = findFirstUsablePosition(lineString);
        if (validPoint) {
          center = validPoint;
          zoom = 10;
          hasValidCoordinates = true;
          console.log("[MAP-ZOOM] Valid MultiLineString coordinates:", { center, zoom });
          break;
        }
      }
    } else if (geoJSONGeometry.type === "Polygon" && Array.isArray(geoJSONGeometry.coordinates)) {
      for (const ring of geoJSONGeometry.coordinates) {
        const validPoint = findFirstUsablePosition(ring);
        if (validPoint) {
          center = validPoint;
          zoom = 10;
          hasValidCoordinates = true;
          console.log("[MAP-ZOOM] Valid Polygon coordinates:", { center, zoom });
          break;
        }
      }
    } else if (geoJSONGeometry.type === "MultiPolygon" && Array.isArray(geoJSONGeometry.coordinates)) {
      for (const polygon of geoJSONGeometry.coordinates) {
        if (!Array.isArray(polygon)) continue;
        for (const ring of polygon) {
          const validPoint = findFirstUsablePosition(ring);
          if (validPoint) {
            center = validPoint;
            zoom = 10;
            hasValidCoordinates = true;
            console.log("[MAP-ZOOM] Valid MultiPolygon coordinates:", { center, zoom });
            break;
          }
        }
        if (hasValidCoordinates) break;
      }
    }
  } catch (error) {
    console.error("[MAP-ZOOM] Error processing geometry:", error);
  }

  return { hasValidCoordinates, center, zoom };
};

/**
 * Calculates the camera state required to fit a bbox within the map viewport.
 *
 * @param params
 * @param params.map - MapLibre map instance (via `MapRef`).
 * @param params.bbox - Bounding box as `[minLng, minLat, maxLng, maxLat]`.
 * @param params.padding - Padding (px) around the bbox. Defaults to `32`.
 *
 * @returns Camera state object, or `null` if the bbox is too large to fit.
 * @returns .zoom - Zoom level needed to fit the bbox; falls back to current map zoom.
 * @returns .center - Map center point to fit the bbox in view.
 * @returns .fitsAtCurrentZoom - `true` if the bbox already fits at the current zoom level.
 */
export function getRequiredZoomForBBox({ map, bbox, padding = 32 }: { map: MapRef; bbox: BBox; padding?: number }) {
  const bounds = [
    [bbox[0], bbox[1]],
    [bbox[2], bbox[3]]
  ] satisfies LngLatBoundsLike;

  const camera = map.cameraForBounds(bounds, { padding });

  // bbox is too large for the map
  if (!camera) return null;

  const requiredZoom = camera.zoom ?? map.getZoom();

  return {
    zoom: requiredZoom, // fallback to current zoom level of the map if camera.zoom failed
    center: camera.center,
    fitsAtCurrentZoom: camera.zoom! <= map.getZoom()
  };
}
